import axios from 'axios'
import { useEffect, useRef, useState } from 'react'
import { serverUrl } from '../../../../../utilites/config'
import { reconnectingSocket } from '../../../../../utilites/socket'

export const DATA_STATUS_TOPIC = '/topic/data-status'

export interface TelescopeDataStatus {
  status: string
  since: Date
}

interface DataStatusSegment {
  status?: unknown
  startTime?: unknown
}

const parseSegment = (segment: DataStatusSegment | null | undefined): TelescopeDataStatus | null => {
  if (!segment || typeof segment.status !== 'string' || typeof segment.startTime !== 'string') {
    return null
  }
  const since = new Date(segment.startTime)
  if (Number.isNaN(since.getTime())) {
    return null
  }
  return { status: segment.status, since }
}

/**
 * Reports the data status reported by the telescope (rosie.status on rudawy), e.g. "Sun Dataflow".
 * Reads the current segment once, because STOMP does not replay the last message to new
 * subscribers, then follows changes pushed on /topic/data-status.
 *
 * @returns The current telescope status, or null when none has been recorded or it is not known yet
 */
export function useTelescopeDataStatus(): TelescopeDataStatus | null {
  const connectionRef = useRef<any>(null)
  const [dataStatus, setDataStatus] = useState<TelescopeDataStatus | null>(null)

  useEffect(() => {
    let isActive = true
    // A pushed change is newer than the initial read, so a late GET response must not overwrite it.
    let receivedPush = false

    axios
      .get<DataStatusSegment>(`${serverUrl}/data-status/current`)
      .then(({ data }) => {
        if (isActive && !receivedPush) {
          setDataStatus(parseSegment(data))
        }
      })
      .catch(error => {
        // 404 means no status has been recorded yet; keep the fallback badge.
        if (error?.response?.status !== 404) {
          console.error('[DataStatus] Failed to read the current data status:', error)
        }
      })

    const handleMessage = (d: any) => {
      try {
        const parsed = parseSegment(JSON.parse(d.body))
        if (isActive && parsed) {
          receivedPush = true
          setDataStatus(parsed)
        }
      } catch (error) {
        console.error('[DataStatus] Error processing data status:', error)
      }
    }

    connectionRef.current = reconnectingSocket(
      serverUrl + '/websocket',
      handleMessage,
      DATA_STATUS_TOPIC,
      window.location.href,
    )

    return () => {
      isActive = false
      connectionRef.current?.disconnect?.()
      connectionRef.current = null
    }
  }, [])

  return dataStatus
}
